/**
 * Données des stages proposés par 1000 ARTS pendant les vacances scolaires.
 * Chaque stage a une page détail à /stages/:id avec flyer, infos utiles et CTA inscription.
 *
 * Champs :
 * - id : slug utilisé dans l'URL
 * - closed : true si le stage est terminé (Inscriptions fermées)
 * - Icon : composant lucide-react
 * - accent / accent_soft : couleurs saison pour l'accent visuel
 * - infos : bullets clefs affichées sur la page détail
 * - tarifs : liste de lignes (label + prix)
 * - description : paragraphe descriptif
 * - contact : numéro affiché en gros pour l'inscription
 */
import { Sun, Leaf } from "lucide-react";

export const STAGES = [
  {
    id: "automne-2026",
    sheet_id: "stage_automne_2026",
    season: "Vacances d'automne",
    title: "Stage d'automne 2026",
    theme: "Hip-hop & Comédie musicale",
    date_label: "Du 12 au 16 octobre 2026",
    date_debut: "2026-10-12",
    date_fin: "2026-10-16",
    age_range: "5 - 14 ans",
    tarif_from: "CHF 450.–",
    Icon: Leaf,
    accent: "#E56B2E",
    accent_soft: "#FDECE0",
    closed: false,
    flyer:
      "/img/709f277f5c.webp",
    description:
      "Une semaine à la croisée du mouvement et de la scène : Hip-hop et Comédie musicale, en deux salles et deux tranches d'âge. Les élèves alternent chorégraphies rythmées et travail vocal/théâtral pour explorer plusieurs facettes de l'art de la scène. La semaine se termine par un mini-spectacle où les élèves présentent leur travail à la famille.",
    infos: [
      { label: "Dates", value: "Du 12 au 16 octobre 2026" },
      { label: "Horaires", value: "9h00 – 16h00" },
      { label: "Groupes", value: "2 salles · 2 tranches d'âge (5-9 ans · 10-14 ans)" },
      { label: "À prévoir", value: "Pique-nique, gourde, tenue confortable, baskets" },
      { label: "Fin de stage", value: "Mini-spectacle à la fin de la semaine" },
    ],
    tarifs: [
      { label: "Tarif membre", value: "CHF 450.–" },
      { label: "Tarif non-membre", value: "CHF 490.–" },
    ],
    parrainage:
      "Les membres 1000 ARTS peuvent parrainer jusqu'à 3 invités extérieurs à l'école (–50.– de réduction par invité parrainé).",
    contact: {
      phone: "079 950 40 31",
      phone_href: "tel:+41799504031",
      email: "info1000arts@gmail.com",
    },
  },
  {
    // Le stage de Pâques 2027 est annulé (école, 30.10.2026) : le prochain stage est celui
    // des vacances d'automne 2027. Dates, programme et prix pas encore fixés : sans
    // `date_debut`, il n'apparaît pas dans Dates importantes. Quand ils le seront, ajouter la
    // ligne `stage_automne_2027` dans Réglages → Stages de la console.
    id: "automne-2027",
    sheet_id: "stage_automne_2027",
    season: "Vacances d'automne",
    title: "Stage d'automne 2027",
    theme: "Programme à venir",
    date_label: "Date à venir — octobre 2027",
    age_range: "À confirmer",
    tarif_from: "À venir",
    Icon: Leaf,
    accent: "#E56B2E",
    accent_soft: "#FDECE0",
    closed: false,
    coming_soon: true,
    annee: 2027,
    flyer: null,
    description:
      "Le prochain stage 1000 ARTS aura lieu pendant les vacances d'automne 2027. La date exacte, le programme (disciplines, tranches d'âge) et les tarifs seront annoncés prochainement. Reviens régulièrement sur cette page pour être averti·e dès l'ouverture des inscriptions.",
    infos: [
      { label: "Dates", value: "Date à venir — octobre 2027" },
      { label: "Programme", value: "Plus d'informations à venir" },
    ],
    tarifs: [],
    contact: {
      phone: "079 950 40 31",
      phone_href: "tel:+41799504031",
      email: "info1000arts@gmail.com",
    },
  },
  {
    id: "ete-2026",
    sheet_id: "stage_ete_2026",
    season: "Vacances d'été",
    title: "Stage d'été 2026",
    theme: "Tennis · Hip-hop",
    date_label: "Du 29 juin au 3 juillet 2026",
    age_range: "6 - 12 ans",
    tarif_from: "CHF 390.–",
    Icon: Sun,
    accent: "#FFB547",
    accent_soft: "#FFF5E1",
    closed: true,
    annee: 2026,
    flyer:
      "/img/4556ee2e19.webp",
    description:
      "Une semaine sportive et artistique en collaboration avec le Green Club de Romanel : tennis le matin, repas et transport inclus, puis hip-hop l'après-midi chez 1000 ARTS. Une aventure idéale pour bouger, découvrir deux disciplines et se faire des amis.",
    infos: [
      { label: "Dates", value: "Du 29 juin au 3 juillet 2026" },
      { label: "Horaires", value: "9h00 – 17h00 (matin & après-midi)" },
      { label: "Matin", value: "🎾 Tennis au Green Club de Romanel (9h - 12h)" },
      { label: "Midi", value: "🍽️ Repas & transport inclus (12h - 14h)" },
      { label: "Après-midi", value: "🕺 Hip-hop chez 1000 ARTS à Cugy (14h - 17h)" },
      { label: "Flexibilité", value: "Possibilité de participer uniquement matin ou après-midi" },
    ],
    tarifs: [
      { label: "Tennis + Hip-hop (journée complète)", value: "CHF 700.–" },
      { label: "Hip-hop uniquement (après-midi)", value: "CHF 390.–" },
    ],
    contact: {
      phone: "079 950 40 31",
      phone_href: "tel:+41799504031",
      email: "info1000arts@gmail.com",
    },
  },
];

// Un stage dont la date de fin est passée est fermé, même avant que la console réponde :
// la page Stages, Dates importantes et les pages texte des robots disent la même chose.
const AUJOURDHUI_CHARGEMENT = new Date().toISOString().slice(0, 10);
STAGES.forEach((s) => { if (s.date_fin && s.date_fin < AUJOURDHUI_CHARGEMENT) s.closed = true; });

/* L'état d'un stage sur le site (Jen, 30.10.2026) : « termine » dès que sa date de fin est
   passée (ou qu'il est fermé), « date_a_venir » tant que sa date n'est pas fixée, sinon
   « ouvert ». Calculé ici : un stage fini ne reste jamais « Inscriptions ouvertes ». */
export function etatStage(s, aujourdhui = new Date().toISOString().slice(0, 10)) {
  if (s.closed || (s.date_fin && s.date_fin < aujourdhui)) return "termine";
  if (s.coming_soon) return "date_a_venir";
  return "ouvert";
}

// L'année d'un stage (sa date, sinon `annee`) et sa place dans l'année (sa date, sinon sa saison).
export const anneeStage = (s) => Number(String(s.date_debut || s.date_fin || "").slice(0, 4)) || s.annee || null;
const MOIS_SAISON = [[/hiver/i, "02"], [/p[aâ]ques|printemps/i, "04"], [/été/i, "07"], [/automne/i, "10"]];
export const cleStage = (s) => s.date_debut
  || `${anneeStage(s)}-${(MOIS_SAISON.find(([re]) => re.test(s.season || "")) || [null, "12"])[1]}`;

/* Les stages par année : les années avec un stage pas encore terminé d'abord (la plus proche
   en premier), puis les années passées (la plus récente en premier) ; dans une année, dans
   l'ordre du calendrier. */
export function stagesParAnnee(liste = STAGES, aujourdhui) {
  const annees = {};
  liste.forEach((s) => { (annees[anneeStage(s)] = annees[anneeStage(s)] || []).push(s); });
  const groupes = Object.entries(annees).map(([annee, stages]) => ({
    annee: Number(annee),
    stages: stages.sort((a, b) => cleStage(a).localeCompare(cleStage(b))),
    a_venir: stages.some((s) => etatStage(s, aujourdhui) !== "termine"),
  }));
  return [
    ...groupes.filter((g) => g.a_venir).sort((a, b) => a.annee - b.annee),
    ...groupes.filter((g) => !g.a_venir).sort((a, b) => b.annee - a.annee),
  ];
}

// Dates, prix et ouverture viennent de la console (Réglages → Catalogue des stages) ;
// les textes et les photos restent ici. Un stage dont la date de fin est passée se ferme
// tout seul, et disparaît donc des pages du site.
const MOIS_STAGE = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août",
  "septembre", "octobre", "novembre", "décembre"];

/* Le premier jour d'un stage, tiré du texte des Réglages (« Du 12 au 16 avril 2027 »,
   « Du 28 mars au 2 avril 2027 », « Le 12 avril 2027 ») et de son dernier jour. Sans lui,
   le site gardait le premier jour écrit à la main (« 5 avril ») avec le dernier jour de la
   console : « du 5 au 16 avril » sur Dates importantes. */
export function debutDepuisTexte(texte, finIso) {
  const fin = /^\d{4}-\d{2}-\d{2}$/.test(finIso || "") ? finIso : "";
  const m = String(texte || "").toLowerCase().match(/(\d{1,2})(?:er)?\s*([a-zéû]+)?/);
  if (!m || !fin) return "";
  const jour = Number(m[1]);
  let annee = Number(fin.slice(0, 4));
  let mois = Number(fin.slice(5, 7));
  const nomMois = MOIS_STAGE.indexOf(m[2] || "");
  if (nomMois >= 0) {
    mois = nomMois + 1;
    if (mois > Number(fin.slice(5, 7))) annee -= 1;
  }
  const iso = `${annee}-${String(mois).padStart(2, "0")}-${String(jour).padStart(2, "0")}`;
  return jour >= 1 && jour <= 31 && iso <= fin ? iso : "";
}

export function appliquerStagesConsoleFiches(liste) {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  (liste || []).forEach((c) => {
    const stage = STAGES.find((s) => s.sheet_id === c.id);
    if (!stage) return;
    if (c.dates) {
      stage.date_label = c.dates;
      stage.infos = (stage.infos || []).map((i) => (i.label === "Dates" ? { ...i, value: c.dates } : i));
    }
    if (c.fin) {
      stage.date_fin = c.fin;
      stage.closed = c.fin < aujourdhui;
      const debut = debutDepuisTexte(c.dates, c.fin);
      if (debut) stage.date_debut = debut;
    }
    if (c.prix_membre) {
      stage.tarif_from = `CHF ${c.prix_membre}.\u2013`;
      stage.tarifs = [
        { label: "Tarif membre", value: `CHF ${c.prix_membre}.\u2013` },
        ...(c.prix_non_membre ? [{ label: "Tarif non-membre", value: `CHF ${c.prix_non_membre}.\u2013` }] : []),
      ];
    }
  });
}

export function getStageById(id) {
  return STAGES.find((s) => s.id === id);
}
